import { useCallback, useEffect, useState } from "react";
import { Trash2, Eye, X, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiError } from "@/lib/api";

const FIELDS = [
  ["family_name", "Family Name"], ["first_name", "First Name"], ["sex", "Sex"],
  ["age", "Age"], ["dob", "Date of Birth"], ["category", "Pageant Category"],
  ["passport", "Passport Number"],
  ["email", "Email"], ["phone", "Mobile"], ["parent_name", "Parent's Name"], ["parents_mobile", "Parent's Mobile"],
  ["address", "Address"], ["city", "City"],
  ["height", "Height (cm)"], ["weight", "Weight (kg)"],
  ["hobby", "Hobby"], ["talent", "Talent"], ["achievements", "Achievements"], ["heard_from", "Heard About Event Via"],
];

const displayName = (r) => `${r.first_name || ""} ${r.family_name || ""}`.trim() || r.name || "Applicant";

export default function AdminRegistrations() {
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/registrations").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markReviewed = async (r) => {
    await api.patch(`/admin/registrations/${r.id}/status`, { status: "reviewed" });
    load();
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the application of ${displayName(r)}? This cannot be undone.`)) return;
    await api.delete(`/admin/registrations/${r.id}`);
    toast.success("Application deleted");
    setActive(null);
    load();
  };

  if (!items) return <div className="py-20 text-center text-sm text-neutral-500">Loading applications...</div>;

  return (
    <div data-testid="registrations-page">
      <h1 className="font-display text-4xl font-medium text-white">Registrations</h1>
      <p className="mt-2 text-sm font-light text-neutral-400">Official pageant applications submitted through the registration form.</p>

      {items.length === 0 ? (
        <div data-testid="registrations-empty" className="mt-8 border border-dashed border-white/15 py-20 text-center text-sm text-neutral-500">
          No applications yet. New registrations will appear here.
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {items.map((r, i) => (
            <div
              key={r.id}
              data-testid={`registration-card-${i}`}
              className={`border p-6 transition-colors ${r.status === "new" ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-white/10 bg-[#0C0C0C]"}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <span data-testid={`registration-name-${i}`} className="font-display text-xl text-white">{displayName(r)}</span>
                    {r.status === "new" && <span className="bg-[#D4AF37] px-2 py-0.5 text-[9px] uppercase tracking-widest text-black">New</span>}
                  </div>
                  <div className="mt-1 text-xs text-[#D4AF37]/90">{r.category}</div>
                  {r.payment_status === "paid" ? (
                    <span data-testid={`registration-paid-${i}`} className="mt-2 inline-block bg-emerald-500/15 px-2 py-0.5 text-[9px] uppercase tracking-widest text-emerald-400">Paid ₹999</span>
                  ) : (
                    <span data-testid={`registration-unpaid-${i}`} className="mt-2 inline-block bg-amber-500/15 px-2 py-0.5 text-[9px] uppercase tracking-widest text-amber-400">Fee Pending</span>
                  )}
                  <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    {r.city && `${r.city} · `}{r.phone} · {r.created_at ? new Date(r.created_at).toLocaleString("en-IN") : ""}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`registration-view-${i}`} onClick={() => setActive(r)} className="flex items-center gap-2 border border-white/15 px-3 py-2 text-xs text-neutral-300 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]">
                    <Eye className="h-4 w-4" strokeWidth={1.5} /> View
                  </button>
                  {r.status === "new" && (
                    <button data-testid={`registration-review-${i}`} onClick={() => markReviewed(r)} className="flex items-center gap-2 border border-white/15 px-3 py-2 text-xs text-neutral-300 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]">
                      <CheckCheck className="h-4 w-4" strokeWidth={1.5} /> Mark Reviewed
                    </button>
                  )}
                  <button data-testid={`registration-delete-${i}`} onClick={() => remove(r)} className="border border-white/15 p-2 text-neutral-300 transition-colors hover:border-red-500 hover:text-red-400" aria-label="Delete">
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
            data-testid="registration-detail-modal"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ y: 32, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto border border-[#D4AF37]/25 bg-[#0C0C0C] p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h2 className="font-display text-3xl text-white">{displayName(active)}</h2>
                  <p className="mt-1 text-xs uppercase tracking-[0.25em] text-[#D4AF37]">{active.category}</p>
                </div>
                <button data-testid="registration-detail-close" onClick={() => setActive(null)} className="text-neutral-400 hover:text-[#D4AF37]" aria-label="Close">
                  <X className="h-5 w-5" strokeWidth={1.5} />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                {FIELDS.filter(([k]) => active[k]).map(([k, label]) => (
                  <div key={k} className="border-b border-white/5 py-3">
                    <div className="text-[9px] uppercase tracking-[0.25em] text-neutral-500">{label}</div>
                    <div className="mt-1 text-sm font-light text-neutral-200">{String(active[k])}</div>
                  </div>
                ))}
              </div>
              {(active.photo_closeup || active.photo_fulllength) && (
                <div className="mt-6 grid grid-cols-2 gap-4">
                  {active.photo_closeup && (
                    <a href={active.photo_closeup} target="_blank" rel="noopener noreferrer" data-testid="registration-photo-closeup">
                      <img src={active.photo_closeup} alt="Close-up" className="h-56 w-full border border-white/10 bg-black object-contain transition-opacity hover:opacity-80" />
                      <span className="mt-2 block text-center text-[10px] uppercase tracking-[0.25em] text-neutral-500">Close-up</span>
                    </a>
                  )}
                  {active.photo_fulllength && (
                    <a href={active.photo_fulllength} target="_blank" rel="noopener noreferrer" data-testid="registration-photo-fulllength">
                      <img src={active.photo_fulllength} alt="Full-length" className="h-56 w-full border border-white/10 bg-black object-contain transition-opacity hover:opacity-80" />
                      <span className="mt-2 block text-center text-[10px] uppercase tracking-[0.25em] text-neutral-500">Full-length</span>
                    </a>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
